import { CELL } from "../../../routes/home/combatCells";
import { legendFor, legendTilesFor, type LegendItem, type LegendSwatch, type LegendTile } from "../../../routes/home/terrainLegend";
import type { SwatchSpec } from "../../../routes/home/terrainArt/swatches";
import { useSwatchUrl } from "../../../routes/home/terrainArt/useSwatch";
import { encounterGround, type EncounterGround, type EncounterState } from "@lancer-vtt/domain";

/** How wide a swatch is drawn, in CSS px: one board cell at zoom 1 ({@link CELL}). */
const SWATCH = CELL;

/** The tone under a swatch: the default map ground's (forest floor), so the translucent cloud
 *  composites as it does on the board. */
const SWATCH_GROUND = "#4b5236";

/** Where the object diamond insets from the swatch edge, in px. */
const SWATCH_PAD = 6;

/**
 * A painted swatch (`terrainArt/swatches.ts`): the terrain painter's own picture of a tile or a wall,
 * so a row shows exactly what the board draws. Until it has painted (or where it cannot, as in a
 * DOM-less test) the frame shows the bare board ground.
 */
function PaintedSwatch({ spec, label }: { spec: SwatchSpec; label: string }) {
  const url = useSwatchUrl(spec);
  return (
    <span className="cw-legend-swatch" role="img" aria-label={`${label} swatch`} style={{ width: SWATCH, height: SWATCH, background: SWATCH_GROUND }}>
      {url && <img src={url} alt="" width={SWATCH} height={SWATCH} />}
    </span>
  );
}

/** A non-tile row's sample: a wall is painted; the object and the cloud are drawn from their specs. */
function Swatch({ swatch, label, uid, ground }: { swatch: LegendSwatch; label: string; uid: string; ground: EncounterGround }) {
  if (swatch.carrier === "edge") return <PaintedSwatch spec={{ kind: "wall", visual: swatch.visual, ground }} label={label} />;
  return (
    <svg className="cw-legend-swatch" width={SWATCH} height={SWATCH} viewBox={`0 0 ${SWATCH} ${SWATCH}`} role="img" aria-label={`${label} swatch`}>
      <rect x={0} y={0} width={SWATCH} height={SWATCH} fill={SWATCH_GROUND} />
      <DrawnMark swatch={swatch} uid={uid} />
    </svg>
  );
}

/** The object diamond or the area cloud, over the ground. `uid` namespaces the cloud's filter id. */
function DrawnMark({ swatch, uid }: { swatch: Exclude<LegendSwatch, { carrier: "edge" }>; uid: string }) {
  if (swatch.carrier === "object") {
    const look = swatch.look;
    const p = SWATCH_PAD;
    const w = SWATCH - 2 * p;
    // The neutral slate diamond every object and deployable wears (`SCENERY_TOKEN_LOOK`).
    const points = [`${p + w / 2},${p}`, `${p + w},${p + w / 2}`, `${p + w / 2},${p + w}`, `${p},${p + w / 2}`].join(" ");
    return <polygon points={points} fill={look.fill} stroke={look.accent} strokeWidth={1.5} />;
  }
  const style = swatch.style;
  // `feGaussianBlur` approximates Pixi's `BlurFilter` at the spec's strength.
  return (
    <>
      <defs>
        <filter id={`lg-cloud-${uid}`}>
          <feGaussianBlur stdDeviation={style.blur / 2} />
        </filter>
      </defs>
      <circle cx={SWATCH / 2} cy={SWATCH / 2} r={SWATCH / 2 - 3} fill={style.fill} filter={`url(#lg-cloud-${uid})`} />
    </>
  );
}

/** One non-tile row: the sample, its name, and what it does to play. */
function Row({ item, ground }: { item: LegendItem; ground: EncounterGround }) {
  return (
    <li className="cw-legend-row" data-legend-class={item.id}>
      <Swatch swatch={item.swatch} label={item.label} uid={item.id} ground={ground} />
      <div className="cw-legend-text">
        <div className="cw-legend-label">{item.label}</div>
        <div className="cw-legend-detail">{item.detail}</div>
      </div>
    </li>
  );
}

/** One tile row: its painted swatch, name, cell count and property chips. */
function TileRow({ tile, ground }: { tile: LegendTile; ground: EncounterGround }) {
  return (
    <li className="cw-legend-row cw-legend-tile" data-legend-tile={tile.id}>
      <PaintedSwatch spec={{ kind: "tile", cell: tile.cell, shape: "chip", ground }} label={tile.name} />
      <div className="cw-legend-text">
        <div className="cw-legend-label">
          <span className="cw-legend-tile-name">{tile.name}</span>
          <span className="cw-legend-tile-count">×{tile.count}</span>
        </div>
        {tile.chips.length > 0 && <div className="cw-legend-tile-chips">{tile.chips.join(" · ")}</div>}
      </div>
    </li>
  );
}

/**
 * The **LEGEND** body — the key to the board's terrain.
 *
 * It lists the **tiles** painted on this board, each with the painter's own swatch, then the walls,
 * objects and clouds (`terrain-tile-art` 5.3). It is **presence-driven**: a tile or wall the board
 * does not use has no row, so the absences carry information too. {@link legendTilesFor} and
 * {@link legendFor} own that decision and every word of the copy; this component owns only how a row
 * looks.
 *
 * **Mode-independent, hosted in the Map key.** A GM reads the key mid-fight in PLAY as often as in
 * EDIT. It renders in the docked Map key panel (`MapKeyPanel.tsx`, `decisions/0191` Ruling 3), an
 * overlay beside the tool rail that costs the board no width and is off until K turns it on.
 * **Read-only and not GM-gated**: the terrain it explains is on every player's screen.
 */
export function TerrainLegendBody({ state }: { state: EncounterState }) {
  const tiles = legendTilesFor(state);
  const items = legendFor(state);
  const bare = tiles.length === 0 && items.length === 0;
  const ground = encounterGround(state);
  return (
    <div className="gms-content cw-legend">
      <div className="cw-hint">{bare ? "Nothing is painted on this board yet — every cell is open ground." : "Only the terrain this board actually uses is listed."}</div>
      {tiles.length > 0 && (
        <section className="cw-legend-tiles" aria-label="tiles on this map">
          <div className="cw-section-name">Tiles on this map</div>
          <ul className="cw-legend-list">
            {tiles.map((t) => (
              <TileRow key={t.key} tile={t} ground={ground} />
            ))}
          </ul>
        </section>
      )}
      {items.length > 0 && (
        <ul className="cw-legend-list">
          {items.map((item) => (
            <Row key={item.id} item={item} ground={ground} />
          ))}
        </ul>
      )}
    </div>
  );
}
